import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ItemsTable } from "@/components/quotation/items-table";
import { QuoteSummary } from "@/components/quotation/quote-summary";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/client/api";
import { convertMinorCurrencyPrecision, currencyOptions, majorToMinor, minorToMajor, normalizeCurrencyCode, type CurrencyCode } from "@/lib/currency/currency";
import { AUTOSAVE_INTERVAL_MS, financialSaveInputs, hasUnsavedChanges, serializeSaveSnapshot } from "@/lib/quotations/autosave";
import { DEFAULT_QUOTE_COLUMNS, type QuoteColumn } from "@/lib/quotations/columns";
import { buildBulkItemsPayload, updateEditableQuoteItem, type EditableQuoteItem, type EditableQuoteItemPatch } from "@/lib/quotations/editor-form";
import { moveItemOrder } from "@/lib/quotations/item-order";
import { workbenchRoutes } from "@/lib/routes/workbench-routes";

const tabs = [
  { id: "project" },
  { id: "items" },
  { id: "financials" },
  { id: "terms" },
  { id: "visual" },
  { id: "link" },
] as const;

type QuoteEditorTab = (typeof tabs)[number]["id"];

const quoteThemeOptions = [
  { id: "vzen_green", color: "#1f4d47" },
  { id: "graphite", color: "#30363d" },
  { id: "navy", color: "#17324d" },
  { id: "burgundy", color: "#6b2737" },
  { id: "teal", color: "#0f5c5c" },
  { id: "custom", color: "#ffffff" },
] as const;

type QuotationRecord = {
  id: string;
  quotationNo: string;
  displayTitle: string | null;
  version: number;
  status: string;
  projectTitle: string;
  clientCompanyId: string | null;
  agencyCompanyId: string | null;
  ownCompanyId: string | null;
  currency: string;
  languageMode: string;
  validUntil: number | null;
  linkExpiresAt: number | null;
  publicToken: string | null;
  publicDownloadPdf: number | boolean;
  publicDownloadExcel: number | boolean;
};

type QuotationFinancials = {
  quotationId: string;
  subtotalMinor: number;
  discountType: string;
  discountValue: number;
  packagePriceEnabled: number | boolean;
  packagePriceMinor: number | null;
  taxMode: string;
  taxRate: number;
  taxAmountMinor: number;
  grandTotalMinor: number;
  currencyCode: CurrencyCode;
  exchangeRateBaseCurrency: CurrencyCode;
  exchangeRate: number;
  exchangeRateSource: string;
  exchangeRateUpdatedAt: number | null;
  updatedAt: number;
};

type QuotationSection = {
  id: string;
  sectionCode: string;
  titleEn: string;
  titleZh: string;
  sortOrder: number;
};

type QuotationItem = EditableQuoteItem & {
  sectionId: string;
};

type QuotationVisual = {
  companyLogoFileId: string | null;
  clientLogoFileId: string | null;
  confidentialLabel: string;
  documentTitleEn: string;
  documentTitleZh: string;
  signatoryRoleEn: string;
  signatoryRoleZh: string;
  watermarkMode: string;
  watermarkText: string | null;
  fontSetId: string | null;
  themePreset: string;
  themePrimaryColor: string | null;
  themePrimaryTextColor: string | null;
  themeSurfaceColor: string | null;
  themeAccentColor: string | null;
  themeBorderColor: string | null;
  omitUnpricedItems: boolean | number;
};

type QuotationTerm = {
  id: string;
  type: string;
  titleEn: string;
  titleZh: string;
  bodyEn: string;
  bodyZh: string;
  included: number | boolean;
  sortOrder: number;
};

type QuotationCoverField = {
  id?: string;
  key: string;
  labelEn: string;
  labelZh: string;
  value: string;
  visible: boolean | number;
  sortOrder: number;
};

type QuotationDetailResponse = {
  quotation: QuotationRecord;
  financials: QuotationFinancials;
  visual: QuotationVisual;
  sections: QuotationSection[];
  items: QuotationItem[];
  terms: QuotationTerm[];
  columns: QuoteColumn[];
  coverFields: QuotationCoverField[];
  capabilities: QuotationCapabilities;
};

type QuotationCapabilities = {
  canView: boolean;
  canEdit: boolean;
  canManageCollaborators: boolean;
  canManageLifecycle: boolean;
};

type Collaborator = {
  userId: string;
  permission: "viewer" | "editor";
  name: string;
  email: string;
  jobTitle: string;
  accountType: "internal" | "external";
};

type CollaboratorUser = Omit<Collaborator, "userId" | "permission"> & { id: string };
type CollaboratorsResponse = { members: Collaborator[]; users: CollaboratorUser[] };

type BulkItemsResponse = {
  ok: true;
  financials: QuotationFinancials;
};

type MutateItemsResponse = {
  item: QuotationItem;
  financials: QuotationFinancials;
};

type MutateSectionResponse = {
  section: QuotationSection;
  items: QuotationItem[];
  financials: QuotationFinancials;
};

type OrderSectionsResponse = {
  sections: Array<{ id: string; sortOrder: number }>;
};

type OrderItemsResponse = {
  items: Array<{ id: string; sortOrder: number }>;
};

type DeleteResponse = {
  ok: true;
  financials: QuotationFinancials;
};

type FontSet = {
  id: string;
  name: string;
};

type FontsResponse = {
  fontSets: FontSet[];
};

type Company = {
  id: string;
  type: "own" | "client" | "agency" | "vendor";
  nameEn: string;
  nameZh: string | null;
  logoFileId: string | null;
};

type CompaniesResponse = {
  companies: Company[];
};

type CatalogModule = {
  code: string;
  titleEn: string;
  titleZh: string;
  sortOrder: number;
};

type ModulesResponse = {
  modules: CatalogModule[];
};

type PublishLinkResponse = {
  id: string;
  token: string;
  url: string;
};

type ShareLink = {
  id: string;
  token: string;
  url: string;
  expiresAt: number | null;
  downloadPdf: boolean;
  downloadExcel: boolean;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
  openCount: number;
};

type ShareLinksResponse = {
  links: ShareLink[];
};

type NewVersionResponse = {
  id: string;
  quotationNo: string;
  version: number;
};

type ExchangeRateResponse = {
  rate: {
    baseCurrency: CurrencyCode;
    quoteCurrency: CurrencyCode;
    rate: number;
    source: string;
    fetchedAt: number;
    providerDate: string | null;
  };
};

export function QuoteEditPage() {
  const { language, t } = useAppSettings();
  const text = language === "zh" ? zh : en;
  const { id } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState<QuotationRecord | null>(null);
  const [capabilities, setCapabilities] = useState<QuotationCapabilities | null>(null);
  const [financials, setFinancials] = useState<QuotationFinancials | null>(null);
  const [visual, setVisual] = useState<QuotationVisual | null>(null);
  const [sections, setSections] = useState<QuotationSection[]>([]);
  const [items, setItems] = useState<QuotationItem[]>([]);
  const [terms, setTerms] = useState<QuotationTerm[]>([]);
  const [fontSets, setFontSets] = useState<FontSet[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [availableModules, setAvailableModules] = useState<CatalogModule[]>([]);
  const [moduleToAdd, setModuleToAdd] = useState("");
  const [activeTab, setActiveTab] = useState<QuoteEditorTab>("project");
  const [quotationNo, setQuotationNo] = useState("");
  const [displayTitle, setDisplayTitle] = useState("");
  const [projectTitle, setProjectTitle] = useState("");
  const [status, setStatus] = useState("draft");
  const [languageMode, setLanguageMode] = useState("en_zh");
  const [validUntil, setValidUntil] = useState("");
  const [clientCompanyId, setClientCompanyId] = useState("");
  const [agencyCompanyId, setAgencyCompanyId] = useState("");
  const [ownCompanyId, setOwnCompanyId] = useState("");
  const [linkPassword, setLinkPassword] = useState("");
  const [linkExpiresAt, setLinkExpiresAt] = useState("");
  const [publicDownloadPdf, setPublicDownloadPdf] = useState(false);
  const [publicUrl, setPublicUrl] = useState("");
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [trashedShareLinks, setTrashedShareLinks] = useState<ShareLink[]>([]);
  const [showLinkTrash, setShowLinkTrash] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [collaboratorUsers, setCollaboratorUsers] = useState<CollaboratorUser[]>([]);
  const [collaboratorUserId, setCollaboratorUserId] = useState("");
  const [collaboratorPermission, setCollaboratorPermission] = useState<"viewer" | "editor">("editor");
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [linkAction, setLinkAction] = useState<string | null>(null);
  const [columns, setColumns] = useState<QuoteColumn[]>(DEFAULT_QUOTE_COLUMNS);
  const [coverFields, setCoverFields] = useState<QuotationCoverField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [duplicatingVersion, setDuplicatingVersion] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [pdfMode, setPdfMode] = useState<"pdf" | "print" | null>(null);
  const [printHtml, setPrintHtml] = useState<string | null>(null);
  const [mutating, setMutating] = useState<string | null>(null);
  const [fetchingRate, setFetchingRate] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autosaveStatus, setAutosaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const lastSavedSnapshotRef = useRef("");
  const currentSnapshotRef = useRef("");
  const saveInProgressRef = useRef(false);
  const saveQuoteRef = useRef<(silent?: boolean) => Promise<boolean>>(async () => false);
  const snapshotInitializedRef = useRef(false);

  useEffect(() => {
    let active = true;
    setPdfMode(null);
    if (id) void apiGet<{ pdfMode: "pdf" | "print" }>(`/api/quotations/${id}/export/capabilities`)
      .then((response) => { if (active) setPdfMode(response.pdfMode); })
      .catch((cause: unknown) => { if (active) setError(cause instanceof Error ? cause.message : "Unable to load export settings / 无法加载导出设置"); });
    return () => { active = false; };
  }, [id]);
  const visibleModuleOptions = useMemo(
    () => availableModules.filter((module) => !sections.some((section) => section.sectionCode === module.code)),
    [availableModules, sections],
  );

  currentSnapshotRef.current = serializeSaveSnapshot({
    quotationNo,
    displayTitle,
    projectTitle,
    status,
    languageMode,
    validUntil,
    clientCompanyId,
    agencyCompanyId,
    ownCompanyId,
    financials: financials ? financialSaveInputs(financials) : null,
    visual,
    columns,
    coverFields,
    items,
    terms,
  });

  useEffect(() => {
    if (moduleToAdd && visibleModuleOptions.some((module) => module.code === moduleToAdd)) {
      return;
    }

    setModuleToAdd(visibleModuleOptions[0]?.code ?? "");
  }, [moduleToAdd, visibleModuleOptions]);

  useEffect(() => {
    let active = true;

    async function loadQuotation() {
      if (!id) {
        return;
      }

      setLoading(true);
      setLoadingLinks(true);
      setError(null);
      snapshotInitializedRef.current = false;
      lastSavedSnapshotRef.current = "";

      try {
        const [response, fontsResponse, companiesResponse, modulesResponse] = await Promise.all([
          apiGet<QuotationDetailResponse>(`/api/quotations/${id}`),
          apiGet<FontsResponse>("/api/fonts"),
          apiGet<CompaniesResponse>("/api/companies"),
          apiGet<ModulesResponse>("/api/modules"),
        ]);

        if (!response.capabilities.canEdit) {
          navigate(workbenchRoutes.quotationPreview(id), { replace: true });
          return;
        }

        const [linksResponse, trashLinksResponse, collaboratorsResponse] = await Promise.all([
          response.capabilities.canManageLifecycle ? apiGet<ShareLinksResponse>(`/api/quotations/${id}/share-links`) : Promise.resolve({ links: [] }),
          response.capabilities.canManageLifecycle ? apiGet<ShareLinksResponse>(`/api/quotations/${id}/share-links?trash=1`) : Promise.resolve({ links: [] }),
          response.capabilities.canManageCollaborators ? apiGet<CollaboratorsResponse>(`/api/quotations/${id}/collaborators`) : Promise.resolve({ members: [], users: [] }),
        ]);

        if (!active) {
          return;
        }

        setQuotation(response.quotation);
        setCapabilities(response.capabilities);
        setFinancials(response.financials);
        setVisual(response.visual);
        setSections(response.sections);
        setItems(response.items);
        setTerms(response.terms);
        setFontSets(fontsResponse.fontSets);
        setCompanies(companiesResponse.companies);
        setAvailableModules(modulesResponse.modules);
        setShareLinks(linksResponse.links);
        setTrashedShareLinks(trashLinksResponse.links);
        setCollaborators(collaboratorsResponse.members);
        setCollaboratorUsers(collaboratorsResponse.users);
        setCollaboratorUserId(collaboratorsResponse.users.find((candidate) => !collaboratorsResponse.members.some((member) => member.userId === candidate.id))?.id ?? "");
        setModuleToAdd(modulesResponse.modules[0]?.code ?? "");
        setQuotationNo(response.quotation.quotationNo);
        setDisplayTitle(response.quotation.displayTitle ?? "");
        setProjectTitle(response.quotation.projectTitle);
        setStatus(response.quotation.status);
        setLanguageMode(response.quotation.languageMode);
        setValidUntil(response.quotation.validUntil ? toDateInputValue(response.quotation.validUntil) : "");
        setClientCompanyId(response.quotation.clientCompanyId ?? "");
        setAgencyCompanyId(response.quotation.agencyCompanyId ?? "");
        setOwnCompanyId(response.quotation.ownCompanyId ?? "");
        setLinkExpiresAt("");
        setPublicDownloadPdf(false);
        setPublicUrl(linksResponse.links[0]?.url ?? (response.quotation.publicToken ? `${window.location.origin}/q/${response.quotation.publicToken}` : ""));
        setColumns(response.columns.length > 0 ? response.columns : DEFAULT_QUOTE_COLUMNS);
        setCoverFields(response.coverFields);
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : t("quoteEdit.loadError"));
        }
      } finally {
        if (active) {
          setLoading(false);
          setLoadingLinks(false);
        }
      }
    }

    void loadQuotation();

    return () => {
      active = false;
    };
  }, [id, navigate, t]);

  useEffect(() => {
    if (loading || !quotation || !financials || !visual || snapshotInitializedRef.current) return;
    lastSavedSnapshotRef.current = currentSnapshotRef.current;
    snapshotInitializedRef.current = true;
    setAutosaveStatus("saved");
  }, [financials, loading, quotation, visual]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (!lastSavedSnapshotRef.current || saveInProgressRef.current) return;
      if (!hasUnsavedChanges(lastSavedSnapshotRef.current, currentSnapshotRef.current)) return;
      void saveQuoteRef.current(true);
    }, AUTOSAVE_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [id]);

  function handleItemChange(itemId: string, patch: EditableQuoteItemPatch) {
    setItems((current) => updateEditableQuoteItem(current, itemId, patch) as QuotationItem[]);
    setMessage(null);
    setError(null);
  }

  async function handleAddItem(sectionId: string) {
    if (!id) return;

    setMutating(`add-item:${sectionId}`);
    setError(null);
    setMessage(null);

    try {
      const response = await apiPost<{ sectionId: string }, MutateItemsResponse>(`/api/quotations/${id}/items`, { sectionId });
      setItems((current) => sortItems([...current, response.item]));
      setFinancials(response.financials);
      setMessage(text.itemAdded);
    } catch (itemError) {
      setError(itemError instanceof Error ? itemError.message : text.itemAddError);
    } finally {
      setMutating(null);
    }
  }

  async function handleDuplicateItem(itemId: string) {
    if (!id) return;

    setMutating(`duplicate-item:${itemId}`);
    setError(null);
    setMessage(null);

    try {
      const response = await apiPost<Record<string, never>, MutateItemsResponse>(`/api/quotations/${id}/items/${itemId}/duplicate`, {});
      setItems((current) => sortItems([...current, response.item]));
      setFinancials(response.financials);
      setMessage(text.itemDuplicated);
    } catch (itemError) {
      setError(itemError instanceof Error ? itemError.message : text.itemDuplicateError);
    } finally {
      setMutating(null);
    }
  }

  async function handleDeleteItem(itemId: string) {
    if (!id) return;

    setMutating(`delete-item:${itemId}`);
    setError(null);
    setMessage(null);

    try {
      const response = await apiDelete<DeleteResponse>(`/api/quotations/${id}/items/${itemId}`);
      setItems((current) => current.filter((item) => item.id !== itemId));
      setFinancials(response.financials);
      setMessage(text.itemDeleted);
    } catch (itemError) {
      setError(itemError instanceof Error ? itemError.message : text.itemDeleteError);
    } finally {
      setMutating(null);
    }
  }

  async function handleMoveItem(itemId: string, direction: "up" | "down") {
    if (!id) return;

    const item = items.find((candidate) => candidate.id === itemId);
    if (!item) return;

    const siblings = items
      .filter((candidate) => candidate.sectionId === item.sectionId)
      .sort((left, right) => getSortOrder(left) - getSortOrder(right));
    const itemIds = moveItemOrder(siblings.map((candidate) => candidate.id), itemId, direction);
    if (!itemIds) return;

    setMutating(`order-items:${item.sectionId}`);
    setError(null);
    setMessage(null);

    try {
      const response = await apiPatch<{ itemIds: string[] }, OrderItemsResponse>(
        `/api/quotations/${id}/sections/${item.sectionId}/items/order`,
        { itemIds },
      );
      const orderById = new Map(response.items.map((orderedItem) => [orderedItem.id, orderedItem.sortOrder]));
      setItems((current) =>
        sortItems(
          current.map((currentItem) => {
            const sortOrder = orderById.get(currentItem.id);
            return sortOrder === undefined ? currentItem : { ...currentItem, sortOrder };
          }),
        ),
      );
      setMessage(text.itemMoved);
    } catch (itemError) {
      setError(itemError instanceof Error ? itemError.message : text.itemMoveError);
    } finally {
      setMutating(null);
    }
  }

  async function handleAddModule() {
    if (!id || !moduleToAdd) return;

    setMutating(`add-module:${moduleToAdd}`);
    setError(null);
    setMessage(null);

    try {
      const response = await apiPost<{ moduleCode: string }, MutateSectionResponse>(`/api/quotations/${id}/sections`, {
        moduleCode: moduleToAdd,
      });
      setSections((current) => [...current, response.section].sort((left, right) => left.sortOrder - right.sortOrder));
      setItems((current) => sortItems([...current, ...response.items]));
      setFinancials(response.financials);
      setMessage(text.moduleAdded);
    } catch (sectionError) {
      setError(sectionError instanceof Error ? sectionError.message : text.moduleAddError);
    } finally {
      setMutating(null);
    }
  }

  async function handleRemoveSection(sectionId: string) {
    if (!id) return;

    setMutating(`delete-section:${sectionId}`);
    setError(null);
    setMessage(null);

    try {
      const response = await apiDelete<DeleteResponse>(`/api/quotations/${id}/sections/${sectionId}`);
      setSections((current) => current.filter((section) => section.id !== sectionId));
      setItems((current) => current.filter((item) => item.sectionId !== sectionId));
      setFinancials(response.financials);
      setMessage(text.moduleRemoved);
    } catch (sectionError) {
      setError(sectionError instanceof Error ? sectionError.message : text.moduleRemoveError);
    } finally {
      setMutating(null);
    }
  }

  async function handleMoveSection(sectionId: string, direction: "up" | "down") {
    if (!id) return;

    const ordered = [...sections].sort((left, right) => left.sortOrder - right.sortOrder);
    const index = ordered.findIndex((section) => section.id === sectionId);
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || targetIndex < 0 || targetIndex >= ordered.length) return;

    const next = [...ordered];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    setMutating("order-sections");
    setError(null);
    setMessage(null);

    try {
      const response = await apiPatch<{ sectionIds: string[] }, OrderSectionsResponse>(
        `/api/quotations/${id}/sections/order`,
        { sectionIds: next.map((section) => section.id) },
      );
      const currentById = new Map(sections.map((section) => [section.id, section]));
      setSections(
        response.sections.flatMap((section) => {
          const current = currentById.get(section.id);
          return current ? [{ ...current, sortOrder: section.sortOrder }] : [];
        }),
      );
      setMessage(text.moduleMoved);
    } catch (sectionError) {
      setError(sectionError instanceof Error ? sectionError.message : text.moduleMoveError);
    } finally {
      setMutating(null);
    }
  }

  function handleTermChange(termId: string, patch: Partial<QuotationTerm>) {
    setTerms((current) => current.map((term) => (term.id === termId ? { ...term, ...patch } : term)));
    setMessage(null);
    setError(null);
  }

  function handleAddTerm() {
    setTerms((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        type: "custom",
        titleEn: "Custom term",
        titleZh: "自定义条款",
        bodyEn: "",
        bodyZh: "",
        included: true,
        sortOrder: (current.length + 1) * 10,
      },
    ]);
  }

  function handleDeleteTerm(termId: string) {
    setTerms((current) => current.filter((term) => term.id !== termId));
  }

  function handleAddCoverField() {
    setCoverFields((current) => [
      ...current,
      {
        key: `custom_${Date.now()}`,
        labelEn: "Custom Field",
        labelZh: "自定义字段",
        value: "",
        visible: true,
        sortOrder: (current.length + 1) * 10,
      },
    ]);
  }

  function updateCoverField(index: number, patch: Partial<QuotationCoverField>) {
    setCoverFields((current) => current.map((field, fieldIndex) => (fieldIndex === index ? { ...field, ...patch } : field)));
  }

  function removeCoverField(index: number) {
    setCoverFields((current) => current.filter((_, fieldIndex) => fieldIndex !== index));
  }

  function updateColumn(index: number, patch: Partial<QuoteColumn>) {
    setColumns((current) => current.map((column, columnIndex) => (columnIndex === index ? { ...column, ...patch } : column)));
  }

  function handleAddColumn() {
    setColumns((current) => [
      ...current,
      {
        key: `custom_${Date.now()}`,
        labelEn: "Custom",
        labelZh: "自定义",
        visible: true,
        sortOrder: (current.length + 1) * 10,
        type: "custom",
      },
    ]);
  }

  function handleRemoveColumn(index: number) {
    const column = columns[index];

    if (!column || column.type === "system") {
      return;
    }

    setColumns((current) => current.filter((_, columnIndex) => columnIndex !== index));
    setItems((current) =>
      current.map((item) => {
        const customValues = { ...(item.customValues ?? {}) };
        delete customValues[column.key];
        return { ...item, customValues };
      }),
    );
  }

  async function handleSave(silent = false): Promise<boolean> {
    if (!id || !financials || !visual || !capabilities?.canEdit || saveInProgressRef.current) {
      return false;
    }

    const payload = buildBulkItemsPayload(items);

    if (!payload.ok) {
      if (!silent) setError(payload.error);
      setAutosaveStatus("error");
      return false;
    }

    const snapshotAtStart = currentSnapshotRef.current;
    saveInProgressRef.current = true;
    setAutosaveStatus("saving");
    if (!silent) {
      setSaving(true);
      setError(null);
      setMessage(null);
    }

    try {
      const quoteResponse = await apiPatch<
        Record<string, unknown>,
        {
          ok: true;
          financials: QuotationFinancials | null;
        }
      >(`/api/quotations/${id}`, {
        quotationNo,
        displayTitle: displayTitle || null,
        projectTitle,
        status,
        languageMode,
        clientCompanyId: clientCompanyId || null,
        agencyCompanyId: agencyCompanyId || null,
        ownCompanyId: ownCompanyId || null,
        validUntil: validUntil ? new Date(`${validUntil}T23:59:59`).getTime() : null,
        financials: financialSaveInputs(financials),
        visual: {
          confidentialLabel: visual.confidentialLabel,
          documentTitleEn: visual.documentTitleEn,
          documentTitleZh: visual.documentTitleZh,
          signatoryRoleEn: visual.signatoryRoleEn,
          signatoryRoleZh: visual.signatoryRoleZh,
          watermarkMode: visual.watermarkMode,
          watermarkText: visual.watermarkText,
          fontSetId: visual.fontSetId,
          themePreset: visual.themePreset,
          themePrimaryColor: visual.themePrimaryColor,
          themePrimaryTextColor: visual.themePrimaryTextColor,
          themeSurfaceColor: visual.themeSurfaceColor,
          themeAccentColor: visual.themeAccentColor,
          themeBorderColor: visual.themeBorderColor,
          omitUnpricedItems: isEnabled(visual.omitUnpricedItems),
          companyLogoFileId: visual.companyLogoFileId,
          clientLogoFileId: visual.clientLogoFileId,
        },
        columns,
        coverFields: coverFields.map((field, index) => ({
          key: field.key,
          labelEn: field.labelEn,
          labelZh: field.labelZh,
          value: field.value,
          visible: isEnabled(field.visible),
          sortOrder: (index + 1) * 10,
        })),
      });
      const response = await apiPost<typeof payload.data, BulkItemsResponse>(`/api/quotations/${id}/items/bulk`, payload.data);
      await apiPatch<
        {
          terms: Array<{
            type: string;
            titleEn: string;
            titleZh: string;
            bodyEn: string;
            bodyZh: string;
            included: boolean;
            sortOrder: number;
          }>;
        },
        { ok: true }
      >(`/api/quotations/${id}/terms/bulk`, {
        terms: terms.map((term, index) => ({
          type: term.type || `term_${index + 1}`,
          titleEn: term.titleEn,
          titleZh: term.titleZh,
          bodyEn: term.bodyEn,
          bodyZh: term.bodyZh,
          included: isEnabled(term.included),
          sortOrder: (index + 1) * 10,
        })),
      });
      setFinancials(response.financials ?? quoteResponse.financials);
      setQuotation((current) =>
        current
          ? {
              ...current,
              quotationNo,
              displayTitle: displayTitle || null,
              projectTitle,
              status,
              languageMode,
              clientCompanyId: clientCompanyId || null,
              agencyCompanyId: agencyCompanyId || null,
              ownCompanyId: ownCompanyId || null,
              validUntil: validUntil ? new Date(`${validUntil}T23:59:59`).getTime() : null,
            }
          : current,
      );
      lastSavedSnapshotRef.current = snapshotAtStart;
      setAutosaveStatus("saved");
      if (!silent) setMessage(t("quoteEdit.saved"));
      return true;
    } catch (saveError) {
      setAutosaveStatus("error");
      if (!silent) setError(saveError instanceof Error ? saveError.message : t("quoteEdit.saveError"));
      return false;
    } finally {
      saveInProgressRef.current = false;
      if (!silent) setSaving(false);
    }
  }

  saveQuoteRef.current = handleSave;

  async function handleFetchExchangeRate() {
    if (!financials) return;

    setFetchingRate(true);
    setError(null);
    setMessage(null);

    try {
      const currencyCode = normalizeCurrencyCode(financials.currencyCode);
      const response = await apiGet<ExchangeRateResponse>(`/api/exchange-rates/latest?base=${encodeURIComponent(currencyCode)}&quote=RMB`);
      setFinancials({
        ...financials,
        currencyCode: response.rate.baseCurrency,
        exchangeRateBaseCurrency: response.rate.quoteCurrency,
        exchangeRate: response.rate.rate,
        exchangeRateSource: response.rate.source,
        exchangeRateUpdatedAt: response.rate.fetchedAt,
      });
      setMessage(text.exchangeRateUpdated);
    } catch (err) {
      setError(err instanceof Error ? err.message : text.exchangeRateError);
    } finally {
      setFetchingRate(false);
    }
  }

  async function handleGenerateLink() {
    if (!id) {
      return;
    }

    setGeneratingLink(true);
    setError(null);
    setMessage(null);

    try {
      const response = await apiPost<
        {
          password: string;
          linkExpiresAt: number | null;
          publicDownloadPdf: boolean;
          publicDownloadExcel: boolean;
        },
        PublishLinkResponse
      >(`/api/quotations/${id}/publish-link`, {
        password: linkPassword,
        linkExpiresAt: linkExpiresAt ? new Date(`${linkExpiresAt}T23:59:59`).getTime() : null,
        publicDownloadPdf,
        publicDownloadExcel: false,
      });
      setPublicUrl(response.url);
      const now = Date.now();
      setShareLinks((current) => [
        {
          id: response.id,
          token: response.token,
          url: response.url,
          expiresAt: linkExpiresAt ? new Date(`${linkExpiresAt}T23:59:59`).getTime() : null,
          downloadPdf: publicDownloadPdf,
          downloadExcel: false,
          createdAt: now,
          updatedAt: now,
          deletedAt: null,
          openCount: 0,
        },
        ...current,
      ]);
      setLinkPassword("");
      setMessage(t("quoteEdit.linkCreated"));
    } catch (linkError) {
      setError(linkError instanceof Error ? linkError.message : t("quoteEdit.linkError"));
    } finally {
      setGeneratingLink(false);
    }
  }

  async function handleTrashShareLink(link: ShareLink) {
    if (!id) return;
    setLinkAction(`trash:${link.id}`);
    setError(null);

    try {
      await apiDelete<{ ok: true }>(`/api/quotations/${id}/share-links/${link.id}`);
      const deletedLink = { ...link, deletedAt: Date.now() };
      const remainingLinks = shareLinks.filter((item) => item.id !== link.id);
      setShareLinks(remainingLinks);
      if (publicUrl === link.url) setPublicUrl(remainingLinks[0]?.url ?? "");
      setTrashedShareLinks((current) => [deletedLink, ...current]);
      setMessage(text.linkDeleted);
    } catch (linkError) {
      setError(linkError instanceof Error ? linkError.message : text.linkActionError);
    } finally {
      setLinkAction(null);
    }
  }

  async function handleRestoreShareLink(link: ShareLink) {
    if (!id) return;
    setLinkAction(`restore:${link.id}`);
    setError(null);

    try {
      await apiPost<Record<string, never>, { ok: true }>(`/api/quotations/${id}/share-links/${link.id}/restore`, {});
      const restoredLink = { ...link, deletedAt: null };
      setTrashedShareLinks((current) => current.filter((item) => item.id !== link.id));
      setShareLinks((current) => [restoredLink, ...current]);
      if (!publicUrl) setPublicUrl(restoredLink.url);
      setMessage(text.linkRestored);
    } catch (linkError) {
      setError(linkError instanceof Error ? linkError.message : text.linkActionError);
    } finally {
      setLinkAction(null);
    }
  }

  async function handleDeleteShareLinkPermanently(link: ShareLink) {
    if (!id || !window.confirm(text.confirmPermanentLinkDelete)) return;
    setLinkAction(`permanent:${link.id}`);
    setError(null);

    try {
      await apiDelete<{ ok: true }>(`/api/quotations/${id}/share-links/${link.id}/permanent`);
      setTrashedShareLinks((current) => current.filter((item) => item.id !== link.id));
      setMessage(text.linkDeletedPermanently);
    } catch (linkError) {
      setError(linkError instanceof Error ? linkError.message : text.linkActionError);
    } finally {
      setLinkAction(null);
    }
  }

  async function handleCopyShareLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setMessage(text.linkCopied);
    } catch {
      setError(text.linkCopyError);
    }
  }

  async function handleCreateNewVersion() {
    if (!id) {
      return;
    }

    setDuplicatingVersion(true);
    setError(null);
    setMessage(null);

    try {
      const response = await apiPost<Record<string, never>, NewVersionResponse>(`/api/quotations/${id}/new-version`, {});
      navigate(workbenchRoutes.quotationEdit(response.id));
    } catch (versionError) {
      setError(versionError instanceof Error ? versionError.message : t("quoteEdit.versionError"));
    } finally {
      setDuplicatingVersion(false);
    }
  }

  async function handleDownloadExport(format: "pdf" | "excel") {
    if (!id || !quotation) {
      return;
    }

    setExporting(format);
    setError(null);
    setMessage(null);

    let printing = false;
    try {
      if (capabilities?.canEdit && hasUnsavedChanges(lastSavedSnapshotRef.current, currentSnapshotRef.current) && !(await handleSave())) return;
      setMessage(null);
      if (format === "pdf" && pdfMode === "print") {
        setPrintHtml(null);
        const response = await fetch(`/api/quotations/${id}/export/html`, { credentials: "include" });
        if (!response.ok) throw new Error(await readExportError(response));
        const html = await response.text();
        printing = true;
        setPrintHtml(html);
        return;
      }
      const response = await fetch(`/api/quotations/${id}/export/${format}`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(await readExportError(response));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = getDownloadFilename(response, quotation.quotationNo, format);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      setMessage(format === "pdf" ? t("quoteEdit.pdfReady") : t("quoteEdit.excelReady"));
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : t("quoteEdit.exportError"));
    } finally {
      if (!printing) setExporting(null);
    }
  }

  async function refreshCollaborators() {
    if (!id || !capabilities?.canManageCollaborators) return;
    const response = await apiGet<CollaboratorsResponse>(`/api/quotations/${id}/collaborators`);
    setCollaborators(response.members);
    setCollaboratorUsers(response.users);
    setCollaboratorUserId(response.users.find((candidate) => !response.members.some((member) => member.userId === candidate.id))?.id ?? "");
  }

  async function handleAssignCollaborator() {
    if (!id || !collaboratorUserId) return;
    setMutating("collaborator");
    setError(null);
    try {
      await apiPost<{ userId: string; permission: "viewer" | "editor" }, { ok: true }>(`/api/quotations/${id}/collaborators`, {
        userId: collaboratorUserId,
        permission: collaboratorPermission,
      });
      await refreshCollaborators();
      setMessage(text.collaboratorSaved);
    } catch (collaboratorError) {
      setError(collaboratorError instanceof Error ? collaboratorError.message : text.collaboratorError);
    } finally {
      setMutating(null);
    }
  }

  async function handleRemoveCollaborator(userId: string) {
    if (!id || !window.confirm(text.confirmRemoveCollaborator)) return;
    setMutating(`collaborator:${userId}`);
    setError(null);
    try {
      await apiDelete<{ ok: true }>(`/api/quotations/${id}/collaborators/${userId}`);
      await refreshCollaborators();
      setMessage(text.collaboratorRemoved);
    } catch (collaboratorError) {
      setError(collaboratorError instanceof Error ? collaboratorError.message : text.collaboratorError);
    } finally {
      setMutating(null);
    }
  }

  function renderActivePanel() {
    if (activeTab === "project") return renderProjectPanel();
    if (activeTab === "items") return renderItemsPanel();
    if (activeTab === "financials") return renderFinancialsPanel();
    if (activeTab === "visual") return renderVisualPanel();
    if (activeTab === "terms") return renderTermsPanel();
    return renderLinkPanel();
  }

  function renderProjectPanel() {
    return (
      <div className="space-y-4">
      <Card className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-4">
        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">{text.quotationNo}</span>
          <Input value={quotationNo} onChange={(event) => setQuotationNo(event.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">{text.displayTitle}</span>
          <Input value={displayTitle} onChange={(event) => setDisplayTitle(event.target.value)} />
        </label>
        <label className="space-y-1 xl:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">{t("quoteEdit.projectTitle")}</span>
          <Input value={projectTitle} onChange={(event) => setProjectTitle(event.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">{t("quoteEdit.languageMode")}</span>
          <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" value={languageMode} onChange={(event) => setLanguageMode(event.target.value)}>
              <option value="en_zh">{text.enZh}</option>
              <option value="en_only">{text.enOnly}</option>
              <option value="zh_only">{text.zhOnly}</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">{t("quoteEdit.status")}</span>
          <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" value={status} onChange={(event) => setStatus(event.target.value)}>
            {["draft", "internal_review", "sent", "revised", "approved", "rejected", "expired", "archived"].map((option) => (
              <option key={option} value={option}>
                {getStatusText(option, language)}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">{t("quoteEdit.validUntil")}</span>
          <Input type="date" value={validUntil} onChange={(event) => setValidUntil(event.target.value)} />
        </label>
        <CompanySelect label={text.ownCompany} companies={companies.filter((company) => company.type === "own")} language={language} value={ownCompanyId} onChange={setOwnCompanyId} />
        <CompanySelect label={text.client} companies={companies.filter((company) => company.type === "client")} language={language} value={clientCompanyId} onChange={setClientCompanyId} />
        <CompanySelect label={text.agency} companies={companies.filter((company) => company.type === "agency")} language={language} value={agencyCompanyId} onChange={setAgencyCompanyId} />
      </Card>
      {visual ? (
        <Card className="space-y-3 p-4">
          <h2 className="text-sm font-semibold">{text.documentSettings}</h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{text.documentTitleEn}</span>
              <Input value={visual.documentTitleEn} onChange={(event) => setVisual({ ...visual, documentTitleEn: event.target.value })} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{text.documentTitleZh}</span>
              <Input value={visual.documentTitleZh} onChange={(event) => setVisual({ ...visual, documentTitleZh: event.target.value })} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{t("quoteEdit.confidentialLabel")}</span>
              <Input value={visual.confidentialLabel} onChange={(event) => setVisual({ ...visual, confidentialLabel: event.target.value })} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{text.signatoryRoleEn}</span>
              <Input value={visual.signatoryRoleEn} onChange={(event) => setVisual({ ...visual, signatoryRoleEn: event.target.value })} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{text.signatoryRoleZh}</span>
              <Input value={visual.signatoryRoleZh} onChange={(event) => setVisual({ ...visual, signatoryRoleZh: event.target.value })} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{t("quoteEdit.watermark")}</span>
              <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" value={visual.watermarkMode} onChange={(event) => setVisual({ ...visual, watermarkMode: event.target.value })}>
                <option value="none">{text.none}</option>
                <option value="confidential">{text.confidential}</option>
                <option value="draft">{text.draft}</option>
                <option value="estimate">{text.estimate}</option>
                <option value="private_confidential">{text.privateConfidential}</option>
                <option value="custom">{text.custom}</option>
              </select>
            </label>
            {visual.watermarkMode === "custom" ? (
              <label className="block space-y-1 md:col-span-2 xl:col-span-3">
                <span className="text-xs font-medium text-muted-foreground">{t("quoteEdit.watermarkText")}</span>
                <Input value={visual.watermarkText ?? ""} onChange={(event) => setVisual({ ...visual, watermarkText: event.target.value || null })} />
              </label>
            ) : null}
          </div>
        </Card>
      ) : null}
      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">{text.coverFields}</h2>
          <Button type="button" variant="secondary" className="h-8 px-2" onClick={handleAddCoverField}>
            {text.addField}
          </Button>
        </div>
        <div className="space-y-2">
          {coverFields.map((field, index) => (
            <div key={`${field.key}-${index}`} className="grid gap-2 md:grid-cols-[1fr_1fr_1.3fr_80px_auto]">
              <Input value={field.labelEn} placeholder={text.labelEn} onChange={(event) => updateCoverField(index, { labelEn: event.target.value })} />
              <Input value={field.labelZh} placeholder={text.labelZh} onChange={(event) => updateCoverField(index, { labelZh: event.target.value })} />
              <Input value={field.value} placeholder={text.value} onChange={(event) => updateCoverField(index, { value: event.target.value })} />
              <label className="flex items-center justify-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4 accent-primary" checked={isEnabled(field.visible)} onChange={(event) => updateCoverField(index, { visible: event.target.checked })} />
                {text.show}
              </label>
              <Button type="button" variant="secondary" className="h-9 px-2 text-destructive" onClick={() => removeCoverField(index)}>
                {text.delete}
              </Button>
            </div>
          ))}
        </div>
      </Card>
      {capabilities?.canManageCollaborators ? (
        <Card className="space-y-3 p-4">
          <div>
            <h2 className="text-sm font-semibold">{text.collaborators}</h2>
            <p className="text-xs text-muted-foreground">{text.collaboratorsDescription}</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-64 flex-1 space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{text.account}</span>
              <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm" value={collaboratorUserId} onChange={(event) => setCollaboratorUserId(event.target.value)}>
                <option value="">{text.selectAccount}</option>
                {collaboratorUsers.filter((candidate) => !collaborators.some((member) => member.userId === candidate.id)).map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>{candidate.name} · {candidate.jobTitle} · {candidate.accountType === "external" ? text.external : text.internal}</option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{text.permission}</span>
              <select className="h-9 rounded-md border border-border bg-background px-3 text-sm" value={collaboratorPermission} onChange={(event) => setCollaboratorPermission(event.target.value as "viewer" | "editor")}>
                <option value="viewer">{text.viewer}</option>
                <option value="editor">{text.editor}</option>
              </select>
            </label>
            <Button type="button" onClick={() => void handleAssignCollaborator()} disabled={!collaboratorUserId || mutating === "collaborator"}>{text.assign}</Button>
          </div>
          <div className="divide-y divide-border rounded-md border border-border">
            {collaborators.length === 0 ? <div className="px-3 py-4 text-sm text-muted-foreground">{text.noCollaborators}</div> : collaborators.map((member) => (
              <div key={member.userId} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 text-sm">
                <div><span className="font-medium">{member.name}</span><span className="ml-2 text-muted-foreground">{member.jobTitle} · {member.email}</span></div>
                <div className="flex items-center gap-2"><Badge>{member.permission === "editor" ? text.editor : text.viewer}</Badge><Button type="button" variant="secondary" className="h-8 px-2 text-destructive" onClick={() => void handleRemoveCollaborator(member.userId)} disabled={mutating === `collaborator:${member.userId}`}>{text.remove}</Button></div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
      </div>
    );
  }

  function renderItemsPanel() {
    return (
      <div className="space-y-4">
        <Card className="space-y-3 p-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">{text.finalColumns}</h2>
            <Button type="button" variant="secondary" className="h-8 px-2" onClick={handleAddColumn}>
              {text.addColumn}
            </Button>
          </div>
          <div className="space-y-2">
            {columns.map((column, index) => (
              <div key={`${column.key}-${index}`} className="grid gap-2 md:grid-cols-[80px_1fr_1fr_110px_auto]">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="h-4 w-4 accent-primary" checked={column.visible} onChange={(event) => updateColumn(index, { visible: event.target.checked })} />
                  {text.show}
                </label>
                <Input value={column.labelEn} onChange={(event) => updateColumn(index, { labelEn: event.target.value })} />
                <Input value={column.labelZh} onChange={(event) => updateColumn(index, { labelZh: event.target.value })} />
                <Badge>{column.type === "system" ? text.systemColumn : text.customColumn}</Badge>
                <Button type="button" variant="secondary" className="h-9 px-2 text-destructive" disabled={column.type === "system"} onClick={() => handleRemoveColumn(index)}>
                  {text.delete}
                </Button>
              </div>
            ))}
          </div>
        </Card>
        <Card className="flex flex-wrap items-end gap-3 p-4">
          <label className="min-w-72 flex-1 space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{text.addModule}</span>
            <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" value={moduleToAdd} onChange={(event) => setModuleToAdd(event.target.value)}>
              {visibleModuleOptions.map((module) => (
                <option key={module.code} value={module.code}>
                  {language === "zh" ? module.titleZh : module.titleEn}
                </option>
              ))}
            </select>
          </label>
          <Button type="button" onClick={() => void handleAddModule()} disabled={!moduleToAdd || mutating?.startsWith("add-module")}>
            {text.addModule}
          </Button>
        </Card>
        <Card className="overflow-x-auto">
          <ItemsTable
            sections={sections}
            items={items}
            onItemChange={handleItemChange}
            onAddItem={(sectionId) => void handleAddItem(sectionId)}
            onMoveSection={(sectionId, direction) => void handleMoveSection(sectionId, direction)}
            onDeleteSection={(sectionId) => void handleRemoveSection(sectionId)}
            onMoveItem={(itemId, direction) => void handleMoveItem(itemId, direction)}
            onDuplicateItem={(itemId) => void handleDuplicateItem(itemId)}
            onDeleteItem={(itemId) => void handleDeleteItem(itemId)}
            mutating={mutating}
            columns={columns}
            currencyCode={financials?.currencyCode}
          />
        </Card>
      </div>
    );
  }

  function renderFinancialsPanel() {
    if (!financials) return null;

    return (
      <div className="grid gap-4 xl:grid-cols-[320px_1fr]">
        <QuoteSummary
          subtotalMinor={financials.subtotalMinor}
          packagePriceEnabled={financials.packagePriceEnabled}
          packagePriceMinor={financials.packagePriceMinor}
          taxAmountMinor={financials.taxAmountMinor}
          taxRate={financials.taxRate}
          grandTotalMinor={financials.grandTotalMinor}
          currencyCode={financials.currencyCode}
          exchangeRate={financials.exchangeRate}
          exchangeRateBaseCurrency={financials.exchangeRateBaseCurrency}
        />
        <Card className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{text.currency}</span>
            <select
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              value={financials.currencyCode}
              onChange={(event) => {
                const nextCurrency = normalizeCurrencyCode(event.target.value);
                const previousCurrency = financials.currencyCode;
                setItems((current) =>
                  current.map((item) => ({
                    ...item,
                    rateMinor: convertMinorCurrencyPrecision(item.rateMinor, previousCurrency, nextCurrency),
                    amountMinor: convertMinorCurrencyPrecision(item.amountMinor, previousCurrency, nextCurrency),
                  })),
                );
                setFinancials({
                  ...financials,
                  subtotalMinor: convertMinorCurrencyPrecision(financials.subtotalMinor, previousCurrency, nextCurrency),
                  packagePriceMinor: financials.packagePriceMinor === null ? null : convertMinorCurrencyPrecision(financials.packagePriceMinor, previousCurrency, nextCurrency),
                  discountValue: financials.discountType === "fixed" ? convertMinorCurrencyPrecision(financials.discountValue, previousCurrency, nextCurrency) : financials.discountValue,
                  taxAmountMinor: convertMinorCurrencyPrecision(financials.taxAmountMinor, previousCurrency, nextCurrency),
                  grandTotalMinor: convertMinorCurrencyPrecision(financials.grandTotalMinor, previousCurrency, nextCurrency),
                  currencyCode: nextCurrency,
                  exchangeRateBaseCurrency: "RMB",
                  exchangeRate: nextCurrency === "RMB" ? 1 : financials.exchangeRate,
                  exchangeRateSource: nextCurrency === "RMB" ? "same_currency" : financials.exchangeRateSource,
                  exchangeRateUpdatedAt: nextCurrency === "RMB" ? Date.now() : financials.exchangeRateUpdatedAt,
                });
              }}
            >
              {currencyOptions.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} · {language === "zh" ? currency.labelZh : currency.labelEn}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{text.exchangeRate}</span>
            <Input
              type="number"
              step="0.0001"
              min="0.0001"
              value={financials.exchangeRate}
              onChange={(event) =>
                setFinancials({
                  ...financials,
                  exchangeRateBaseCurrency: "RMB",
                  exchangeRate: Number(event.target.value),
                  exchangeRateSource: "manual",
                  exchangeRateUpdatedAt: Date.now(),
                })
              }
            />
          </label>
          <div className="space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">{text.exchangeRateSource}</span>
            <Button type="button" variant="secondary" className="h-9 w-full" onClick={() => void handleFetchExchangeRate()} disabled={fetchingRate}>
              {fetchingRate ? text.fetchingRate : text.fetchExchangeRate}
            </Button>
            <p className="text-xs text-muted-foreground">
              1 {financials.currencyCode} = {Number(financials.exchangeRate || 1).toFixed(4)} {financials.exchangeRateBaseCurrency}
              {financials.exchangeRateUpdatedAt ? ` · ${formatDateTime(financials.exchangeRateUpdatedAt)}` : ""}
            </p>
          </div>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{t("quoteEdit.taxMode")}</span>
            <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" value={financials.taxMode} onChange={(event) => setFinancials({ ...financials, taxMode: event.target.value })}>
              <option value="no_tax">{text.noTax}</option>
              <option value="add_tax">{text.addTax}</option>
              <option value="tax_included">{text.taxIncluded}</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{t("quoteEdit.taxRate")}</span>
            <Input type="number" step="0.01" min="0" max="1" value={financials.taxRate} onChange={(event) => setFinancials({ ...financials, taxRate: Number(event.target.value) })} />
          </label>
          <label className="mt-5 flex h-9 items-center gap-2 rounded-md border border-border px-3 text-sm">
            <input type="checkbox" className="h-4 w-4 accent-primary" checked={isEnabled(financials.packagePriceEnabled)} onChange={(event) => setFinancials({ ...financials, packagePriceEnabled: event.target.checked })} />
            {t("quoteEdit.packagePrice")}
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{text.packagePriceMinor}</span>
            <Input
              type="number"
              min="0"
              step={financials.currencyCode === "JPY" ? "1" : "0.01"}
              value={minorToMajor(financials.packagePriceMinor ?? 0, financials.currencyCode)}
              onChange={(event) => setFinancials({ ...financials, packagePriceMinor: majorToMinor(event.target.value, financials.currencyCode) })}
            />
          </label>
        </Card>
      </div>
    );
  }

  function renderVisualPanel() {
    if (!visual) return null;

    return (
      <Card className="space-y-6 p-4">
        <section className="space-y-3">
          <h2 className="text-sm font-semibold">{text.brandStyle}</h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{t("quoteEdit.fontSet")}</span>
              <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" value={visual.fontSetId ?? ""} onChange={(event) => setVisual({ ...visual, fontSetId: event.target.value || null })}>
                {fontSets.map((fontSet) => (
                  <option key={fontSet.id} value={fontSet.id}>{fontSet.name}</option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{text.companyLogoFileId}</span>
              <Input value={visual.companyLogoFileId ?? ""} onChange={(event) => setVisual({ ...visual, companyLogoFileId: event.target.value || null })} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">{text.clientLogoFileId}</span>
              <Input value={visual.clientLogoFileId ?? ""} onChange={(event) => setVisual({ ...visual, clientLogoFileId: event.target.value || null })} />
            </label>
          </div>
        </section>
        <section className="space-y-3 border-t border-border pt-5">
          <div>
            <h2 className="text-sm font-semibold">{text.colorTheme}</h2>
            <p className="mt-1 text-xs text-muted-foreground">{text.colorThemeDescription}</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {quoteThemeOptions.map((theme) => (
              <button
                key={theme.id}
                type="button"
                className={`flex h-10 items-center gap-2 rounded-md border px-3 text-left text-sm transition ${visual.themePreset === theme.id ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-border hover:bg-muted"}`}
                onClick={() => setVisual({ ...visual, themePreset: theme.id })}
              >
                <span className="h-4 w-4 shrink-0 rounded-sm border border-black/10" style={{ backgroundColor: theme.color }} aria-hidden="true" />
                <span>{text.themeNames[theme.id]}</span>
              </button>
            ))}
          </div>
          {visual.themePreset === "custom" ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              <ColorInput label={text.primaryColor} value={visual.themePrimaryColor ?? "#1f4d47"} onChange={(value) => setVisual({ ...visual, themePrimaryColor: value })} />
              <ColorInput label={text.primaryTextColor} value={visual.themePrimaryTextColor ?? "#ffffff"} onChange={(value) => setVisual({ ...visual, themePrimaryTextColor: value })} />
              <ColorInput label={text.surfaceColor} value={visual.themeSurfaceColor ?? "#e8efed"} onChange={(value) => setVisual({ ...visual, themeSurfaceColor: value })} />
              <ColorInput label={text.accentColor} value={visual.themeAccentColor ?? "#fff2a8"} onChange={(value) => setVisual({ ...visual, themeAccentColor: value })} />
              <ColorInput label={text.borderColor} value={visual.themeBorderColor ?? "#bfc8c5"} onChange={(value) => setVisual({ ...visual, themeBorderColor: value })} />
            </div>
          ) : null}
        </section>
        <section className="space-y-3 border-t border-border pt-5">
          <div>
            <h2 className="text-sm font-semibold">{text.outputOptions}</h2>
            <p className="mt-1 text-xs text-muted-foreground">{text.outputOptionsDescription}</p>
          </div>
          <label className="flex max-w-xl items-start gap-3 rounded-md border border-border px-3 py-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-primary"
              checked={isEnabled(visual.omitUnpricedItems)}
              onChange={(event) => setVisual({ ...visual, omitUnpricedItems: event.target.checked })}
            />
            <span>
              <span className="block font-medium">{text.omitUnpricedItems}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">{text.omitUnpricedItemsDescription}</span>
            </span>
          </label>
        </section>
        <div className="flex gap-2 border-t border-border pt-5">
          <Button type="button" variant="secondary" disabled={exporting !== null || saving || autosaveStatus === "saving" || Boolean(mutating) || pdfMode === null} onClick={() => void handleDownloadExport("pdf")}>
            {exporting === "pdf" ? t("quoteEdit.exporting") : pdfMode === "print" ? (language === "zh" ? "打印 / 保存 PDF" : "Print / Save PDF") : t("quoteEdit.pdf")}
          </Button>
          <Button type="button" variant="secondary" disabled={exporting !== null || saving || autosaveStatus === "saving" || Boolean(mutating)} onClick={() => void handleDownloadExport("excel")}>
            {exporting === "excel" ? t("quoteEdit.exporting") : t("quoteEdit.excel")}
          </Button>
        </div>
      </Card>
    );
  }

  function renderTermsPanel() {
    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button type="button" onClick={handleAddTerm}>{text.addTerm}</Button>
        </div>
        {terms.map((term) => (
          <Card key={term.id} className="space-y-2 p-3">
            <div className="grid gap-2 md:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_140px_auto] md:items-center">
              <label className="flex h-9 items-center gap-2 whitespace-nowrap rounded-md border border-border px-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={isEnabled(term.included)}
                  onChange={(event) => handleTermChange(term.id, { included: event.target.checked })}
                />
                {text.includeTerm}
              </label>
              <Input value={term.titleEn} placeholder={text.titleEn} onChange={(event) => handleTermChange(term.id, { titleEn: event.target.value })} />
              <Input value={term.titleZh} placeholder={text.titleZh} onChange={(event) => handleTermChange(term.id, { titleZh: event.target.value })} />
              <Input value={term.type} placeholder={text.type} onChange={(event) => handleTermChange(term.id, { type: event.target.value })} />
              <Button type="button" variant="secondary" onClick={() => handleDeleteTerm(term.id)}>{text.delete}</Button>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <Textarea className="min-h-16 resize-y" value={term.bodyEn} placeholder={text.bodyEn} onChange={(event) => handleTermChange(term.id, { bodyEn: event.target.value })} />
              <Textarea className="min-h-16 resize-y" value={term.bodyZh} placeholder={text.bodyZh} onChange={(event) => handleTermChange(term.id, { bodyZh: event.target.value })} />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  function renderLinkPanel() {
    const visibleLinks = showLinkTrash ? trashedShareLinks : shareLinks;

    return (
      <div className="space-y-4">
        <Card className="space-y-3 p-4">
          <h2 className="text-sm font-semibold">{t("quoteEdit.clientLink")}</h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <Input type="password" value={linkPassword} placeholder={t("quoteEdit.linkPassword")} onChange={(event) => setLinkPassword(event.target.value)} />
            <Input type="date" aria-label={text.linkExpiry} value={linkExpiresAt} onChange={(event) => setLinkExpiresAt(event.target.value)} />
            <label className="flex h-9 items-center gap-2 rounded-md border border-border px-3 text-sm">
              <input type="checkbox" className="h-4 w-4 accent-primary" checked={publicDownloadPdf} onChange={(event) => setPublicDownloadPdf(event.target.checked)} />
              {t("quoteEdit.allowPdf")}
            </label>
          </div>
          <Button type="button" disabled={generatingLink || linkPassword.length < 6} onClick={() => void handleGenerateLink()}>
            {generatingLink ? t("quoteEdit.generatingLink") : t("quoteEdit.generateLink")}
          </Button>
          {publicUrl ? <Input readOnly value={publicUrl} /> : null}
        </Card>

        <Card className="overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold">{text.linkManagement}</h2>
              <p className="text-xs text-muted-foreground">{text.linkManagementDescription}</p>
            </div>
            <div className="flex gap-2">
              <Button type="button" className="h-8 px-2" variant={!showLinkTrash ? "primary" : "secondary"} onClick={() => setShowLinkTrash(false)}>{text.activeLinks}</Button>
              <Button type="button" className="h-8 px-2" variant={showLinkTrash ? "primary" : "secondary"} onClick={() => setShowLinkTrash(true)}>{text.linkTrash} ({trashedShareLinks.length})</Button>
            </div>
          </div>
          {loadingLinks ? <div className="px-4 py-8 text-sm text-muted-foreground">{text.loadingLinks}</div> : null}
          {!loadingLinks && visibleLinks.length === 0 ? <div className="px-4 py-8 text-sm text-muted-foreground">{text.noLinks}</div> : null}
          {!loadingLinks
            ? visibleLinks.map((link) => (
                <div key={link.id} className="grid gap-3 border-b border-border px-4 py-3 last:border-b-0 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-center">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{link.url}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {text.createdAt} {formatDateTime(link.createdAt)} · {text.expiresAt} {link.expiresAt ? formatDateTime(link.expiresAt) : text.never} · PDF {link.downloadPdf ? text.allowed : text.disabled} · Excel {link.downloadExcel ? text.allowed : text.disabled}
                    </div>
                  </div>
                  <div className="text-sm"><span className="font-semibold">{link.openCount}</span> {text.opens}</div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {!showLinkTrash ? (
                      <>
                        <Button type="button" className="h-8 px-2" variant="secondary" onClick={() => void handleCopyShareLink(link.url)}>{text.copyLink}</Button>
                        <Button type="button" className="h-8 px-2" variant="secondary" disabled={linkAction === `trash:${link.id}`} onClick={() => void handleTrashShareLink(link)}>{text.delete}</Button>
                      </>
                    ) : (
                      <>
                        <Button type="button" className="h-8 px-2" variant="secondary" disabled={linkAction === `restore:${link.id}`} onClick={() => void handleRestoreShareLink(link)}>{text.restore}</Button>
                        <Button type="button" variant="secondary" className="h-8 px-2 text-destructive" disabled={linkAction === `permanent:${link.id}`} onClick={() => void handleDeleteShareLinkPermanently(link)}>{text.deletePermanently}</Button>
                      </>
                    )}
                  </div>
                </div>
              ))
            : null}
        </Card>
      </div>
    );
  }

  if (loading) {
    return <Card className="p-6 text-sm text-muted-foreground">{t("quoteEdit.loading")}</Card>;
  }

  if (!quotation) {
    return <Card className="p-6 text-sm text-muted-foreground">{error ?? t("quoteEdit.notFound")}</Card>;
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">{displayTitle || quotation.quotationNo}</h1>
            <Badge>{getStatusText(status, language)}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">{quotationNo} · {projectTitle} · {financials?.currencyCode ?? quotation.currency} · {getLanguageModeText(languageMode, text)}</p>
        </div>
        <div className="flex gap-2">
          <span className="self-center text-xs text-muted-foreground">{getAutosaveStatusText(autosaveStatus, text, hasUnsavedChanges(lastSavedSnapshotRef.current, currentSnapshotRef.current))}</span>
          <Link
            to={workbenchRoutes.quotationPreview(quotation.id)}
            className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition hover:bg-muted"
          >
            {t("quoteEdit.preview")}
          </Link>
          <Button type="button" onClick={() => void handleSave()} disabled={saving || exporting !== null}>
            {saving ? t("quoteEdit.saving") : t("quoteEdit.save")}
          </Button>
          <Button type="button" variant="secondary" onClick={() => void handleCreateNewVersion()} disabled={duplicatingVersion}>
            {duplicatingVersion ? t("quoteEdit.creatingVersion") : t("quoteEdit.newVersion")}
          </Button>
        </div>
      </div>
      {message ? <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">{message}</div> : null}
      {error ? <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div> : null}
      <div className="flex gap-2 overflow-x-auto border-b border-border">
        {tabs.filter((tab) => tab.id !== "link" || capabilities?.canManageLifecycle).map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? "h-10 whitespace-nowrap border-b-2 border-primary px-2 text-sm font-medium text-foreground" : "h-10 whitespace-nowrap border-b-2 border-transparent px-2 text-sm text-muted-foreground hover:text-foreground"}
            onClick={() => setActiveTab(tab.id)}
          >
            {text.tabs[tab.id]}
          </button>
        ))}
      </div>
      <fieldset disabled={exporting !== null} className="min-w-0">{renderActivePanel()}</fieldset>
      {printHtml !== null ? <iframe title="Quotation print / 报价打印" className="fixed -left-[10000px] top-0 h-[1123px] w-[794px]" tabIndex={-1} aria-hidden="true" sandbox="allow-same-origin allow-modals" srcDoc={printHtml} onLoad={(event) => {
        const frame = event.currentTarget;
        void (async () => {
          let timeout: ReturnType<typeof setTimeout> | undefined;
          try {
            const doc = frame.contentDocument;
            if (!doc || !frame.contentWindow) throw new Error("Print document unavailable / 打印文档不可用");
            await Promise.race([
              Promise.all([doc.fonts.ready, ...Array.from(doc.images).map((img) => img.complete ? Promise.resolve() : new Promise<void>((resolve) => { img.onload = () => resolve(); img.onerror = () => resolve(); }))]),
              new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error("Print assets timed out / 打印资源加载超时")), 15000); }),
            ]);
            frame.contentWindow.focus();
            frame.contentWindow.print();
          } catch (cause) { setError(cause instanceof Error ? cause.message : t("quoteEdit.exportError")); }
          finally { clearTimeout(timeout); setExporting(null); }
        })();
      }} /> : null}
    </section>
  );
}

function toDateInputValue(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function formatDateTime(timestamp: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

function ColorInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex h-9 items-center gap-2 rounded-md border border-border px-2">
        <input type="color" className="h-6 w-8 cursor-pointer border-0 bg-transparent p-0" value={value} onChange={(event) => onChange(event.target.value)} />
        <span className="font-mono text-xs uppercase text-muted-foreground">{value}</span>
      </div>
    </label>
  );
}

function CompanySelect({
  companies,
  label,
  language,
  onChange,
  value,
}: {
  companies: Company[];
  label: string;
  language: "en" | "zh";
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className="space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">-</option>
        {companies.map((company) => (
          <option key={company.id} value={company.id}>
            {language === "zh" ? company.nameZh || company.nameEn : company.nameEn}
          </option>
        ))}
      </select>
    </label>
  );
}

function sortItems(items: QuotationItem[]): QuotationItem[] {
  return [...items].sort((left, right) => {
    if (left.sectionId !== right.sectionId) {
      return left.sectionId.localeCompare(right.sectionId);
    }

    return getSortOrder(left) - getSortOrder(right);
  });
}

function getSortOrder(item: QuotationItem): number {
  const sortOrder = "sortOrder" in item && typeof item.sortOrder === "number" ? item.sortOrder : 0;
  return sortOrder;
}

function isEnabled(value: number | boolean | null | undefined): boolean {
  return value === true || value === 1;
}

async function readExportError(response: Response): Promise<string> {
  const text = await response.text();

  try {
    const parsed = JSON.parse(text) as { error?: string };
    return parsed.error ?? text;
  } catch {
    return text || `Export failed with status ${response.status}.`;
  }
}

function getDownloadFilename(response: Response, quotationNo: string, format: "pdf" | "excel"): string {
  const disposition = response.headers.get("content-disposition");
  const match = disposition?.match(/filename="([^"]+)"/);

  if (match?.[1]) {
    return match[1];
  }

  return format === "pdf" ? `${quotationNo}.pdf.html` : `${quotationNo}.xlsx`;
}

function getStatusText(status: string, language: "en" | "zh"): string {
  const labels: Record<string, { en: string; zh: string }> = {
    draft: { en: "Draft", zh: "草稿" },
    internal_review: { en: "Internal Review", zh: "内部审核" },
    sent: { en: "Sent", zh: "已发送" },
    revised: { en: "Revised", zh: "已修订" },
    approved: { en: "Approved", zh: "已通过" },
    rejected: { en: "Rejected", zh: "已拒绝" },
    expired: { en: "Expired", zh: "已过期" },
    archived: { en: "Archived", zh: "已归档" },
  };

  return labels[status]?.[language] ?? status;
}

function getLanguageModeText(languageMode: string, text: typeof en): string {
  if (languageMode === "zh_only") return text.zhOnly;
  if (languageMode === "en_only") return text.enOnly;
  return text.enZh;
}

function getAutosaveStatusText(status: "idle" | "saving" | "saved" | "error", text: typeof en, hasPendingChanges: boolean): string {
  if (status === "saving") return text.autosaving;
  if (status === "error") return text.autosaveError;
  if (hasPendingChanges) return text.unsavedChanges;
  if (status === "saved") return text.autosaved;
  return "";
}

const en = {
  tabs: {
    project: "Project Info",
    items: "Modules & Items",
    financials: "Financials",
    visual: "Style & Export",
    terms: "Terms",
    link: "Link",
  },
  quotationNo: "Quotation number",
  displayTitle: "Quote display name",
  coverFields: "Signoff page fields",
  addField: "Add field",
  addColumn: "Add column",
  finalColumns: "Final quote columns",
  labelEn: "Label EN",
  labelZh: "Label ZH",
  value: "Value",
  show: "Show",
  delete: "Delete",
  collaborators: "Collaborators",
  collaboratorsDescription: "Assign internal or external accounts at the quotation-group level. New versions inherit these permissions.",
  account: "Account",
  selectAccount: "Select an account",
  permission: "Permission",
  viewer: "View only",
  editor: "Can edit",
  assign: "Assign",
  internal: "Internal",
  external: "External",
  noCollaborators: "No collaborators assigned.",
  collaboratorSaved: "Collaborator assigned.",
  collaboratorRemoved: "Collaborator removed.",
  collaboratorError: "Unable to update collaborator.",
  confirmRemoveCollaborator: "Remove this collaborator from the quotation?",
  systemColumn: "System",
  customColumn: "Custom",
  ownCompany: "Own company",
  client: "Client",
  agency: "Agency",
  addModule: "Add module",
  remove: "Remove",
  moveUp: "Move module up",
  moveDown: "Move module down",
  enZh: "English + Chinese",
  enOnly: "English only",
  zhOnly: "Chinese only",
  noTax: "No tax",
  addTax: "Add tax",
  taxIncluded: "Tax included",
  none: "None",
  fixed: "Fixed",
  percentage: "Percentage",
  packagePriceMinor: "Package price",
  documentTitleEn: "Document title EN",
  documentTitleZh: "Document title ZH",
  documentSettings: "Document & signoff settings",
  signatoryRoleEn: "Submitter role EN",
  signatoryRoleZh: "Submitter role ZH",
  brandStyle: "Brand & typography",
  colorTheme: "Quote color theme",
  colorThemeDescription: "Applied to quote title bars, table headers, borders, and the negotiated package-price row.",
  outputOptions: "Output options",
  outputOptionsDescription: "Control what appears in preview, public links, and exports.",
  omitUnpricedItems: "Automatically hide unpriced details",
  omitUnpricedItemsDescription: "When enabled, zero-amount line items and modules that have no priced lines are hidden from client-facing output.",
  themeNames: {
    vzen_green: "VZEN Green",
    graphite: "Graphite",
    navy: "Navy",
    burgundy: "Burgundy",
    teal: "Teal",
    custom: "Custom",
  },
  primaryColor: "Title color",
  primaryTextColor: "Title text",
  surfaceColor: "Table header",
  accentColor: "Highlight",
  borderColor: "Border",
  currency: "Currency",
  exchangeRate: "Reference exchange rate",
  exchangeRateSource: "Exchange rate",
  fetchExchangeRate: "Fetch rate",
  fetchingRate: "Fetching...",
  exchangeRateUpdated: "Exchange rate updated.",
  exchangeRateError: "Unable to fetch exchange rate.",
  confidential: "Confidential",
  draft: "Draft",
  estimate: "Estimate",
  privateConfidential: "Private confidential",
  custom: "Custom",
  companyLogoFileId: "Company logo file ID",
  clientLogoFileId: "Client logo file ID",
  addTerm: "Add term",
  includeTerm: "Include",
  titleEn: "Title EN",
  titleZh: "Title ZH",
  type: "Type",
  bodyEn: "Body EN",
  bodyZh: "Body ZH",
  itemAdded: "Item added.",
  itemAddError: "Unable to add item.",
  itemDuplicated: "Item duplicated.",
  itemDuplicateError: "Unable to duplicate item.",
  itemDeleted: "Item deleted.",
  itemDeleteError: "Unable to delete item.",
  itemMoved: "Item order saved.",
  itemMoveError: "Unable to reorder items.",
  moduleAdded: "Module added.",
  moduleAddError: "Unable to add module.",
  moduleRemoved: "Module removed.",
  moduleRemoveError: "Unable to remove module.",
  moduleMoved: "Module order saved.",
  moduleMoveError: "Unable to reorder modules.",
  linkExpiry: "Link expiry date",
  linkManagement: "Generated links",
  linkManagementDescription: "Manage access, downloads, expiry, and successful opens for each client link.",
  activeLinks: "Active",
  linkTrash: "Trash",
  loadingLinks: "Loading links...",
  noLinks: "No links in this view.",
  createdAt: "Created",
  expiresAt: "Expires",
  never: "Never",
  allowed: "Allowed",
  disabled: "Disabled",
  opens: "opens",
  copyLink: "Copy",
  restore: "Restore",
  deletePermanently: "Delete permanently",
  linkDeleted: "Link moved to trash.",
  linkRestored: "Link restored.",
  linkDeletedPermanently: "Link permanently deleted.",
  linkCopied: "Link copied.",
  linkCopyError: "Unable to copy link.",
  linkActionError: "Unable to update link.",
  confirmPermanentLinkDelete: "Permanently delete this client link? This cannot be undone.",
  autosaving: "Autosaving...",
  autosaved: "All changes saved",
  autosaveError: "Autosave failed",
  unsavedChanges: "Unsaved changes",
};

const zh: typeof en = {
  tabs: {
    project: "项目信息",
    items: "模块与明细",
    financials: "财务设置",
    visual: "样式与导出",
    terms: "条款",
    link: "客户链接",
  },
  quotationNo: "报价编号",
  displayTitle: "报价显示名称",
  coverFields: "签署页字段",
  addField: "添加字段",
  addColumn: "添加列",
  finalColumns: "最终报价列",
  labelEn: "英文标签",
  labelZh: "中文标签",
  value: "内容",
  show: "显示",
  delete: "删除",
  collaborators: "报价协作者",
  collaboratorsDescription: "按报价组分配公司内部或外部协作账号，新版本会自动继承这些权限。",
  account: "账号",
  selectAccount: "选择账号",
  permission: "权限",
  viewer: "仅查看",
  editor: "可编辑",
  assign: "分配",
  internal: "公司内部",
  external: "外部协作",
  noCollaborators: "尚未分配协作者。",
  collaboratorSaved: "协作者已分配。",
  collaboratorRemoved: "协作者已移除。",
  collaboratorError: "无法更新协作者。",
  confirmRemoveCollaborator: "确认将此协作者从报价中移除？",
  systemColumn: "系统列",
  customColumn: "自定义列",
  ownCompany: "己方公司",
  client: "客户",
  agency: "代理商",
  addModule: "添加模块",
  remove: "移除",
  moveUp: "上移模块",
  moveDown: "下移模块",
  enZh: "中英文",
  enOnly: "仅英文",
  zhOnly: "仅中文",
  noTax: "不含税",
  addTax: "另加税",
  taxIncluded: "含税",
  none: "无",
  fixed: "固定金额",
  percentage: "百分比",
  packagePriceMinor: "打包价",
  documentTitleEn: "英文文档标题",
  documentTitleZh: "中文文档标题",
  documentSettings: "文档与签署设置",
  signatoryRoleEn: "递交方角色（英文）",
  signatoryRoleZh: "递交方角色（中文）",
  brandStyle: "品牌与字体",
  colorTheme: "报价单颜色主题",
  colorThemeDescription: "应用于报价单标题栏、表头、边框及打包价强调行。",
  outputOptions: "输出选项",
  outputOptionsDescription: "控制预览、客户链接和导出文件中显示的内容。",
  omitUnpricedItems: "自动剔除未填写部分",
  omitUnpricedItemsDescription: "勾选后，零金额明细和没有已报价明细的模块不会出现在面向客户的报价输出中。",
  themeNames: {
    vzen_green: "VZEN 绿",
    graphite: "石墨黑",
    navy: "海军蓝",
    burgundy: "酒红",
    teal: "青绿",
    custom: "自定义",
  },
  primaryColor: "标题栏颜色",
  primaryTextColor: "标题文字颜色",
  surfaceColor: "表头背景色",
  accentColor: "强调色",
  borderColor: "边框颜色",
  currency: "币种",
  exchangeRate: "参考汇率",
  exchangeRateSource: "汇率来源",
  fetchExchangeRate: "自动获取汇率",
  fetchingRate: "获取中...",
  exchangeRateUpdated: "汇率已更新。",
  exchangeRateError: "无法获取汇率。",
  confidential: "保密",
  draft: "草稿",
  estimate: "估价",
  privateConfidential: "私密保密",
  custom: "自定义",
  companyLogoFileId: "公司 Logo 文件 ID",
  clientLogoFileId: "客户 Logo 文件 ID",
  addTerm: "添加条款",
  includeTerm: "采用",
  titleEn: "英文标题",
  titleZh: "中文标题",
  type: "类型",
  bodyEn: "英文正文",
  bodyZh: "中文正文",
  itemAdded: "项目已添加。",
  itemAddError: "无法添加项目。",
  itemDuplicated: "项目已复制。",
  itemDuplicateError: "无法复制项目。",
  itemDeleted: "项目已删除。",
  itemDeleteError: "无法删除项目。",
  itemMoved: "项目顺序已保存。",
  itemMoveError: "无法调整项目顺序。",
  moduleAdded: "模块已添加。",
  moduleAddError: "无法添加模块。",
  moduleRemoved: "模块已移除。",
  moduleRemoveError: "无法移除模块。",
  moduleMoved: "模块顺序已保存。",
  moduleMoveError: "无法调整模块顺序。",
  linkExpiry: "链接有效期",
  linkManagement: "已生成链接",
  linkManagementDescription: "分别管理每个客户链接的权限、有效期和成功打开次数。",
  activeLinks: "有效链接",
  linkTrash: "回收站",
  loadingLinks: "正在加载链接...",
  noLinks: "当前没有链接。",
  createdAt: "创建于",
  expiresAt: "有效期至",
  never: "永久",
  allowed: "允许",
  disabled: "禁用",
  opens: "次打开",
  copyLink: "复制",
  restore: "恢复",
  deletePermanently: "彻底删除",
  linkDeleted: "链接已移入回收站。",
  linkRestored: "链接已恢复。",
  linkDeletedPermanently: "链接已彻底删除。",
  linkCopied: "链接已复制。",
  linkCopyError: "无法复制链接。",
  linkActionError: "无法更新链接。",
  confirmPermanentLinkDelete: "确定彻底删除此客户链接吗？此操作无法撤销。",
  autosaving: "正在自动保存...",
  autosaved: "所有更改已保存",
  autosaveError: "自动保存失败",
  unsavedChanges: "有未保存更改",
};
